import IOption from 'models/Option';
import { ChangeEvent, FC, forwardRef, ReactNode } from 'react';

type InputProps = {
  id: string;
  name: string;
  placeholder?: string;
  value: string;
  onChange: (event:ChangeEvent<HTMLInputElement>) => void;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
  type?: string;
  className?: string;
  style?: React.CSSProperties;
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
};

export const Input:FC<InputProps> = ({
  // Props
  id, name, placeholder = '', value, onChange, onKeyPress, error = false, errorText = '', disabled = false, type = 'text', className = '', style = {}
}) => {
  return (
    <div className="mb-4">
      <input
        className={`
          outline-none
          w-full h-12
          bg-gray-200 rounded-2xl
          border ${error ? 'border-red-500 focus:border-red-500' : 'border-gray-300 focus:border-primary'}
          shadow-neu-inner
          text-black
          text-base
          px-4 py-3
          placeholder:text-gray-400 appearance-none
          ${className}
        `}
        name={name} id={id} value={value} placeholder={placeholder} type={type}
        onChange={onChange}
        onKeyPress={onKeyPress}
        disabled={disabled}
        style={style}
      />
      {error && errorText ? (
        <span className="block text-red-500 text-xs pl-4 pt-2">{errorText}</span>
      ) : null}
    </div>
  )
}

type CheckboxProps = {
  className?: string;
  id?: string;
  name: string;
  checked: boolean;
  size?: number;
  onChange: (event:ChangeEvent<HTMLInputElement>) => void;
  children?: ReactNode;
}

export const Checkbox:FC<CheckboxProps> = ({
  // Props
  className = '', id, name, checked, size = 3.5, onChange, children
}) => {
  return (
    <div className={`flex items-center gap-4 ${className}`}>
      <input
        type="checkbox"
        id={id} name={name}
        checked={checked}
        onChange={onChange}
        className={`w-${size} h-${size}`}
      />
      {children || null}
    </div>
  )
}

type SelectProps = {
  className?: string;
  id: string;
  name: string;
  placeholder?: string;
  options: IOption[];
  value: IOption['value'];
  onChange?: (event:React.ChangeEvent<HTMLSelectElement>) => void;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
};

export const Select = forwardRef<any, SelectProps>(({
  className = 'mb-4', id, value = '', options, disabled = false, ...props
}, ref) => {
  return (
    <div className="mb-4">
      <select
        {...props}
        ref={ref}
        id={id}
        disabled={disabled}
        value={value}
        className={`
          select
          outline-none
          w-full h-12
          rounded-2xl
          border border-gray-300 focus:border-primary
          shadow-neu-inner
          text-black
          text-base
          px-4 py-3
          placeholder:text-gray-400
          ${disabled ? 'opacity-90 cursor-default' : ''}
        `}
      >
        {options.map((option, index:number) => (
          <option
            key={`${props.name}-option-item-${index}`}
            value={option.value}
            disabled={option.disabled}
          >{option.label}</option>
        ))}
      </select>
    </div>
  )
});
